// Before React, so a throw during the first render is still reported (00d).
import "./sentry.js"
import "./styles/global.css"

import {
  createNookClient,
  createNookQueryClient,
  type NookApiError,
  QueryClientProvider,
} from "@nook/api-client"
import { ReactQueryDevtools } from "@nook/api-client/devtools"
import { isAuthApiError } from "@supabase/supabase-js"
import { RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"

import { supabase } from "./lib/supabase.js"
import { router } from "./router.js"

let checking: Promise<void> | null = null

/**
 * A 401 on any call that sent a token: the server may no longer honour a session
 * `localStorage` still holds, which `_authed` would keep letting through.
 *
 * `account_deleted` goes to `/signin`, which shows why and signs out there; under
 * `_auth` the 401 is that layout's own `beforeLoad`'s, already handled. Anything else is
 * confirmed with Supabase first, because the backend 401s on any `getUser`
 * failure, transient ones included, and dropping the session signs the
 * extension out too. One check at a time, however many calls failed.
 */
function onUnauthorized(error: NookApiError): void {
  if (error.code === "account_deleted") {
    const [matched] = router.getMatchedRoutes(window.location.pathname)
    if (matched.some((route) => route.id === "/_auth")) return
    void router.navigate({ href: "/signin?error_code=account_deleted" })
    return
  }
  checking ??= supabase.auth
    .getUser()
    .then(async ({ error }) => {
      if (!isAuthApiError(error) || error.status === 429) return
      await supabase.auth.signOut({ scope: "local" })
    })
    .finally(() => (checking = null))
}

/**
 * Configure the API client once, before anything renders (block 00e).
 *
 * The webapp is the only place that knows where its backend is and how it gets
 * a token — `@nook/api-client` is handed both and learns neither. `getSession`
 * refreshes an expired token on its own, so nothing here tracks expiry.
 */
createNookClient({
  baseUrl: import.meta.env.VITE_API_URL,
  // Recorded on every nook this client captures, and declared once here so no
  // call site can pass it — or get it wrong (block 03).
  surface: "webapp",
  authTokenProvider: async () =>
    (await supabase.auth.getSession()).data.session?.access_token ?? null,
  onUnauthorized,
})

// `false` here and `false` in the extension and mobile: a webapp tab can sit
// open for hours, so a refocus should revalidate (plan §9.1).
const queryClient = createNookQueryClient({ refetchOnWindowFocus: false })

/**
 * Broadcast the session to this window, on every page and every auth change
 * (block 01), and re-run the route guards (block 02).
 *
 * This is how the extension signs in: its popup opens `/signin` in a tab, and a
 * content script listening on this origin relays what is posted here into
 * `chrome.storage.local`. The webapp does not know the extension exists — no
 * extension ID, no query flag, no branch. Anything else listening on this
 * origin could read the message, but anything running on this page can already
 * read the same session out of `localStorage`, so nothing new is exposed.
 *
 * It fires for `INITIAL_SESSION` too, so an already-signed-in tab hands the
 * session over on load, and posts `null` on sign-out — which is what makes the
 * extension sign out with the webapp rather than holding a dead token.
 */
supabase.auth.onAuthStateChange((_event, session) => {
  window.postMessage({ type: "nook-session", session }, window.location.origin)

  // One reactive path off the protected shell, whatever caused the change: the
  // account menu, another tab signing out, or a refresh token that expired.
  // `clear()` first so the next signed-in user cannot read the previous one's
  // cached `/v1/me`; `invalidate()` re-runs `_authed`'s `beforeLoad`, which is
  // what actually performs the redirect.
  queryClient.clear()
  void router.invalidate()
})

// The extension's panel signed out (`session-bridge.content.ts`). Its sign-out
// already revoked this session server-side; this just drops the local copy.
window.addEventListener("message", (event: MessageEvent<{ type?: string } | null>) => {
  if (event.source === window && event.data?.type === "nook-sign-out") {
    void supabase.auth.signOut({ scope: "local" })
  }
})

const root = document.getElementById("root")

if (!root) {
  throw new Error("#root is missing from index.html")
}

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <ReactQueryDevtools />
    </QueryClientProvider>
  </StrictMode>,
)
